import React, { useEffect, useState, useRef } from 'react'

//Locator-specific imports.
import { getLocationInfoFromZip } from 'services/locator'
import LocationListItem from 'steps/locator/LocationListItem'
import Map from 'steps/locator/Map'
import './Locator.scss'
import LocationMapMarker from 'steps/locator/LocationMapMarker'
import LocatorForm from 'steps/locator/LocatorForm'
import LocationCountBlurb from 'steps/locator/LocationCountBlurb'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { randomuuidv4 } from 'util/maths'
import { useTranslation } from 'i18n'

//Misc util imports
import analytics from 'global/AnalyticsData'

import {
  start as serviceStart,
  complete as serviceComplete,
  awardPoints,
} from 'services/locator'

const LocatorStep = ({
  brk,
  step,
  breakId,
  onStepStart,
  onEngage = function () {},
  onAwardPoints = function () {},
  onStepSkip,
  scrollComplete,
  isComplete,
  onAuthRequired,
  onUnhandledError,
  skipButton,
  nextStepButton,
}) => {
  const { t } = useTranslation('locator')
  const searchRadius = 25

  const client = step.client || undefined //Sponsored client ID.

  const [locationInfo, setLocationInfo] = useState([])
  const [appMessage, setAppMessage] = useState('') //an error message.
  const [formTouched, setFormTouched] = useState(false) //If form has been modified by user.
  const [stepCompleted, setStepCompleted] = useState(false) //Step progress state
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationHover, setLocationHover] = useState(null)

  const sliderRef = useRef(null)
  const nonceRef = useRef(null)
  const selfRef = useRef(null)
  const sentStartRef = useRef(false)

  const sliderSettings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
  }

  const [inView, entry] = useIntersectionObserver(selfRef, {
    threshold: 0.4,
  })

  const handleStartClick = async () => {
    try {
      const response = await serviceStart(breakId, step.id)
      nonceRef.current = response.detail.nonce
      if (onStepStart) {
        const { attempt, firstBreakEngagement } = response
        onStepStart(step, attempt, firstBreakEngagement)
      }
    } catch (e) {
      if (e.message === '401') {
        onAuthRequired && onAuthRequired()
      } else {
        onUnhandledError && onUnhandledError()
      }
    }
  }
  const onLocationHover = thisLocation => {
    //setLocationHover(thisLocation.id)
  }

  useEffect(() => {
    if (!step.hasStartScreen && !sentStartRef.current && inView) {
      handleStartClick()
      sentStartRef.current = true
    }
  }, [step.hasStartScreen, inView, handleStartClick])

  //listen for scrollComplete and fire off handleCompleteClick when toggled
  useEffect(() => {
    if (scrollComplete && !isComplete) {
      onEngage()
      onAwardPoints()
    }
  }, [scrollComplete, isComplete, breakId, step.id])

  //unlock arrow if alreadyComplete
  useEffect(() => {
    if (isComplete) {
      setStepCompleted(true)
    }
  }, [isComplete])

  const onLocationClicked = locationEmitted => {
    console.log('event: step_interaction_locator_directions_lookup')
    analytics.stepEvent('step_interaction_locator_directions_lookup', step, {
      context: locationEmitted.name,
      context_id: locationEmitted.id,
      context_client: locationEmitted.client,
    })
  }

  //Triggered when a marker selection is made with the location list children.
  const changeSelectedLocation = location => {
    if (location !== null) {
      setSelectedLocation(location.id)
      setStepCompleted(true)
      console.log('event: step_interaction_locator_marker_selection')
      analytics.stepEvent('step_interaction_locator_marker_selection', step, {
        context: location.name,
        context_id: location.id,
        context_client: location.client,
      })
    } else {
      setSelectedLocation(null)
    }
  }

  //Reformatting locations data so that google maps can read it.
  const locationsConstructor = locs => {
    let l2 = locs.map(function (location) {
      location.coordinates = {
        lat: location.gpsLocation[1],
        lng: location.gpsLocation[0],
      }
      delete location.gpsLocation
      if (!location.id) {
        location.id = randomuuidv4()
      }
      return location
    })
    return l2
  }

  //User inputs zipcode on form, we look it up and populate locations.
  const lookupZipCode = async function (zip) {
    onEngage()
    onAwardPoints()
    try {
      setFormTouched(true)
      setStepCompleted(true)
      let params = {
        client: client,
        zip: zip,
        distance: searchRadius || 25,
      }
      let resp = await getLocationInfoFromZip(params)
      let locations = await locationsConstructor(resp.detail)
      setLocationInfo(locations)
      // if (formTouched === false) {
      //   onAwardPoints()
      // }

      analytics.stepEvent('step_interaction_locator_button_click', step)
    } catch (e) {
      setAppMessage(`Error getting location information: ${e}`)
    }
  }

  return (
    <>
      <section className="locator-step">
        <div className="locator--message">{appMessage}</div>
        <div className="locator">
          <div
            className={`step-content with-shadow ${
              brk?.navigationType === 'TOP' ? 'top-navigation' : ''
            }`}
          >
            {client ? (
              <>
                <div className="locator--header">
                  <div className="locator--title">
                    {
                      /* headline */ step.headline && (
                        <h2
                          className="locator-step--headline step-content__title"
                          dangerouslySetInnerHTML={{ __html: step.headline }}
                        ></h2>
                      )
                    }
                    {
                      /* title subhead */ step.introCopy && (
                        <h3
                          className="locator-step--subhead"
                          dangerouslySetInnerHTML={{ __html: step.introCopy }}
                        ></h3>
                      )
                    }
                  </div>
                  <LocatorForm onZipCodeChanged={lookupZipCode} step={step} />
                </div>
                <div className="locator--content">
                  <div className="locator--map-container">
                    <Map locations={locationInfo}>
                      {locationInfo.map(location => {
                        return (
                          <LocationMapMarker
                            key={location.id}
                            isSelected={location.id === selectedLocation}
                            changeSelectedLocation={changeSelectedLocation}
                            location={location}
                            step={step}
                            className={`dgsdgsdgsdg`}
                            isHovered={location.id === locationHover}
                          >
                            <LocationListItem
                              key={location.id}
                              isSelected={location.id === selectedLocation}
                              isInSidebar={false}
                              location={location}
                              onLocationClicked={onLocationClicked}
                              changeSelectedLocation={changeSelectedLocation}
                            />
                          </LocationMapMarker>
                        )
                      })}
                    </Map>
                  </div>
                  <div className="locator--locations-list-container">
                    <div className="locator--locations-list-positioner">
                      <div className="locator--locations-list">
                        <LocationCountBlurb
                          locationInfo={locationInfo}
                          formTouched={formTouched}
                        />
                        {locationInfo.map(location => {
                          return (
                            <LocationListItem
                              key={location.id}
                              isSelected={location.id === selectedLocation}
                              isInSidebar={true}
                              location={location}
                              onLocationClicked={onLocationClicked}
                              changeSelectedLocation={changeSelectedLocation}
                              onLocationHover={onLocationHover}
                            />
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className={`error-text`}>
                {t('error.clientNotDefined')}
              </div>
            )}
            {skipButton}
            {nextStepButton}
          </div>
        </div>
      </section>
    </>
  )
}

export default LocatorStep
